// frontend/src/components/admin/SentryTab.tsx
import { useQueryClient } from "@tanstack/react-query";
import { BarChart, Bar, Tooltip, ResponsiveContainer } from "recharts";
import {
  useAdminSentrySummary,
  type SentryTopIssue,
  type SentrySparklineBucket,
} from "@/hooks/useAdminSentry";
import { relativeTime } from "@/components/admin/UserDetailDrawer";

function issueUrl(orgSlug: string, issueId: string): string {
  return `https://sentry.io/organizations/${orgSlug}/issues/${issueId}/`;
}

function allIssuesUrl(orgSlug: string): string {
  return `https://sentry.io/organizations/${orgSlug}/issues/?query=is:unresolved&statsPeriod=24h`;
}

function Tile({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-4">
      <div className="text-xs uppercase tracking-wide text-zinc-400 mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

function TileOpen({ count }: { count: number }) {
  const color =
    count > 200 ? "text-red-400" : count > 50 ? "text-amber-400" : "text-zinc-100";
  return (
    <Tile label="Open issues (unresolved)">
      <div className={`text-4xl font-semibold ${color}`}>{count}</div>
    </Tile>
  );
}

function TileNew24h({ count }: { count: number }) {
  const color =
    count > 200 ? "text-red-400" : count > 50 ? "text-amber-400" : "text-zinc-100";
  return (
    <Tile label="New in last 24h">
      <div className={`text-4xl font-semibold ${color}`}>{count}</div>
    </Tile>
  );
}

function TileTop3({
  issues,
  orgSlug,
}: {
  issues: SentryTopIssue[];
  orgSlug: string;
}) {
  return (
    <Tile label="Top 3 by frequency (24h)">
      {issues.length === 0 ? (
        <div className="text-sm text-zinc-500">No unresolved issues — nice.</div>
      ) : (
        <ul className="space-y-2">
          {issues.map((i) => (
            <li key={i.id}>
              <a
                href={issueUrl(orgSlug, i.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:bg-zinc-800 rounded px-2 py-1 -mx-2 -my-1 transition-colors"
              >
                <div className="text-sm text-zinc-100 line-clamp-2">{i.title}</div>
                <div className="text-xs text-zinc-400 mt-0.5">
                  {i.count_24h} events · last seen {relativeTime(i.last_seen_at)}
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Tile>
  );
}

function TileSparkline({ buckets }: { buckets: SentrySparklineBucket[] }) {
  const data = buckets.map((b) => ({
    hour: new Date(b.bucket_start).getUTCHours(),
    count: b.count,
  }));
  return (
    <Tile label="Events per hour (24h)">
      <div style={{ width: "100%", height: 120 }}>
        <ResponsiveContainer>
          <BarChart data={data}>
            <Tooltip
              contentStyle={{ background: "#18181b", border: "1px solid #3f3f46" }}
              formatter={(v) => [`${v} events`, ""]}
              labelFormatter={(h) => `${h}:00 UTC`}
            />
            <Bar dataKey="count" fill="#10b981" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Tile>
  );
}

export function SentryTab() {
  const { data, isLoading } = useAdminSentrySummary();
  const queryClient = useQueryClient();

  if (isLoading || !data) {
    return <div className="text-sm text-zinc-400">Loading Sentry summary…</div>;
  }

  if (!data.configured) {
    return (
      <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-6 text-sm text-zinc-300">
        <div className="font-medium mb-2">Sentry not configured</div>
        <div className="text-zinc-400">
          Sentry isn't configured in this environment. Set{" "}
          <code className="text-zinc-200">SENTRY_AUTH_TOKEN</code>,{" "}
          <code className="text-zinc-200">SENTRY_ORG_SLUG</code>, and{" "}
          <code className="text-zinc-200">SENTRY_PROJECT_SLUG</code> on Railway
          to enable this tab.
        </div>
      </div>
    );
  }

  if (data.error === "sentry_unreachable") {
    return (
      <div className="rounded-lg border border-amber-700 bg-amber-950/40 p-6 text-sm">
        <div className="font-medium text-amber-200 mb-2">
          Sentry temporarily unreachable
        </div>
        <div className="text-amber-300 mb-4">
          The Sentry API is not responding. The panel will retry automatically in
          60s, or you can refresh now.
        </div>
        <button
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: ["admin", "sentry", "summary"],
            })
          }
          className="rounded border border-amber-600 bg-amber-900/40 px-3 py-1 text-amber-100 hover:bg-amber-900/70"
        >
          Refresh
        </button>
      </div>
    );
  }

  const tiles = data.tiles!;
  const orgSlug = data.org_slug!;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <TileOpen count={tiles.issues_open} />
        <TileNew24h count={tiles.issues_new_24h} />
        <TileTop3 issues={tiles.top_3} orgSlug={orgSlug} />
        <TileSparkline buckets={tiles.sparkline} />
      </div>
      <div className="text-xs text-zinc-500 flex items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <span>
            Sentry data fetched{" "}
            {data.fetched_at ? relativeTime(data.fetched_at) : "just now"} · 60s
            server-side TTL
          </span>
          <button
            type="button"
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: ["admin", "sentry", "summary"],
              })
            }
            className="rounded border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-zinc-300 hover:bg-zinc-800"
            title="Force refetch (bypasses client cache; backend may still serve its 60s cache)"
          >
            Refresh
          </button>
        </span>
        <a
          href={allIssuesUrl(orgSlug)}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-zinc-300"
        >
          View all in Sentry →
        </a>
      </div>
    </div>
  );
}
